import OfferSchedule from '../../../OfferSchedule';
import { previewScheduleInstant } from '../../../OfferSchedule/dates';
import useOfferImagePreview, { OfferImagePreviewStatus } from '../../../../useOfferImagePreview';
import RedemptionCodeDisplay from '../../../RedemptionCodeDisplay';
import React from 'react';
import {
  Box,
  Typography,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  styled,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import OfferCard from 'components/offers/OfferCard';
import Skeleton from '@mui/material/Skeleton';
import { configureOfferPreviewCardProps } from '../../../OfferWizard/utils';
import { getOfferFulfillmentLabel } from 'modules/Business/modules/Offers/fulfillment';

const CustomDialog = styled(Dialog)({
  "& .MuiDialog-scrollPaper": {
    alignItems: "flex-start",
  },
});

const pillSx = (theme) => ({
  px: 2,
  py: 0.5,
  borderRadius: '9999px',
  fontSize: 14,
  background: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
  color: theme.palette.text.primary,
  display: 'inline-block',
  fontWeight: 500,
  mr: 1,
  mb: 1,
});

const EmptyField = ({ width = '40%' }) => (
  <Skeleton component="span" width={width} sx={{ display: 'inline-block' }} />
);

const PreviewBody = ({ details, isMobile, theme }) => {
  const offerDetails = details?.offerDetails || details;
  const offerLocation = offerDetails?.offerLocation || details?.offerLocation;

  // Same logic as Offer Details / Offer Review: show call/location icons when enabled
  const localList = offerLocation?.local;
  const showPhone = Array.isArray(localList) && localList.some(loc => loc.showPhone === true);
  const showAddress = Array.isArray(localList) && localList.some(loc => loc.showAddress === true);
  const phoneForCard = (() => {
    const withPhone = Array.isArray(localList) && localList.find(loc => loc.showPhone === true);
    return withPhone?.phoneNumber ?? withPhone?.phone ?? null;
  })();

  return { offerDetails, offerLocation, showPhone, showAddress, phoneForCard };
};

const periodLabel = (value) => {
  const normalized = String(value || '').toUpperCase();
  if (normalized === '1' || normalized === 'DAY' || normalized === 'DAILY') return 'daily';
  if (normalized === '2' || normalized === 'WEEK' || normalized === 'WEEKLY') return 'weekly';
  if (normalized === '3' || normalized === 'TOTAL' || normalized === 'CAMPAIGN') return 'total campaign';
  return normalized ? normalized.toLowerCase() : '';
};

const formatAmount = (value, period, showMissingPeriod = false) => {
  if (value == null) return null;
  if (Number(value) === 0) return 'No limit';
  const label = periodLabel(period);
  return `$${Number(value).toLocaleString()}${
    label ? ` ${label}` : showMissingPeriod ? ' — period not set' : ''
  }`;
};

const formatClaims = (value, period, showMissingPeriod = false, unlimitedClaims = false) => {
  if (unlimitedClaims) return 'No limit';
  if (value == null) return null;
  const label = periodLabel(period);
  return `${Number(value).toLocaleString()}${
    label ? ` ${label}` : showMissingPeriod ? ' — period not set' : ''
  }`;
};

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const hasUnlimitedClaims = (details, budget) => {
  if (details?.unlimitedClaims === true || budget?.unlimitedClaims === true) return true;
  if (hasOwn(details, 'unlimitedClaims') || hasOwn(budget, 'unlimitedClaims')) return false;
  return Boolean(budget) && budget.maxClick == null && budget.clickPeriod == null;
};

const formatOfferLocation = (value, fallback) => {
  if (fallback) return fallback;
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (value.raw) return value.raw;
  if (value.type === 'US') return 'Nationwide';
  if (value.type === 'GLOBAL') return 'Global';
  if (value.type === 'state' && Array.isArray(value.states) && value.states.length) {
    return `States: ${value.states.join(', ')}`;
  }
  if (value.type === 'zip' && Array.isArray(value.zips) && value.zips.length) {
    return `ZIP codes: ${value.zips.join(', ')}`;
  }
  if (value.type === 'local' && Array.isArray(value.local) && value.local.length) {
    const radii = value.local
      .map((item) => item?.radius)
      .filter((radius) => radius != null && radius !== '');
    if (radii.length) {
      return radii.length === 1
        ? `Within ${radii[0]} miles`
        : radii.map((radius) => `Within ${radius} miles`).join(', ');
    }
    return 'Local service area';
  }
  return value.type || '';
};

const siteHref = (url) => {
  if (!url) return '';
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const OfferPreviewCard = ({
  details,
  onClose,
  variant = 'dialog',
  businessId: suppliedBusinessId,
  useBusinessDefault = false,
  showEmptyFields = false,
  compact = false,
  showBusinessAndType = true,
  showBusinessLogo = true,
  businessLogoUrl,
  fillAvailableHeight = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')) || compact;
  const shouldFillAvailableHeight = fillAvailableHeight && !isMobile;
  const businessLogo = useSelector((state) =>
    get(state, 'business.companyInfo.logoUrl')
  );

  const { offerDetails, offerLocation, showPhone, showAddress, phoneForCard } =
    PreviewBody({ details, isMobile, theme });
  const currentBusinessId = useSelector((state) => state.business?.businessId ?? state.business?.companyInfo?.businessId);
  const imagePreview = useOfferImagePreview(
    suppliedBusinessId ?? offerDetails?.businessId ?? currentBusinessId,
    offerDetails?.offerImage || offerDetails?.imageUrl,
    useBusinessDefault,
  );
  const locationLabel = formatOfferLocation(offerLocation, details?.locationLabel);
  const fulfillmentLabel = getOfferFulfillmentLabel(offerDetails?.fulfillment);
  const categories = Array.isArray(details?.category) ? details.category : [];
  const budgetLabel = formatAmount(
    details?.budget?.maxAmount,
    details?.budget?.amountPeriod,
    showEmptyFields
  );
  const budget = details?.budget || offerDetails?.budget;
  const unlimitedClaims = hasUnlimitedClaims(details, budget);
  const claimsLabel = formatClaims(
    budget?.maxClick,
    budget?.clickPeriod,
    showEmptyFields,
    unlimitedClaims
  );
  const previewStartDate = previewScheduleInstant(details?.schedule?.startAt || details?.startDate);
  const previewEndDate = previewScheduleInstant(details?.schedule?.endAt || details?.endDate, true);

  const offerCardProps = {
    ...details,
    ...configureOfferPreviewCardProps,
    isPreview: true,
    imageUrl: imagePreview.imageUrl,
    businessImageUrl: showBusinessLogo ? (businessLogoUrl !== undefined ? businessLogoUrl : businessLogo) : null,
    endDate: previewEndDate,
    claimsRemaining: budget?.maxClick,
    unlimitedClaims,
    showPhone,
    showAddress,
    phone: phoneForCard || (showEmptyFields ? '(555) 000-0000' : null),
    address: showEmptyFields ? 'Business address' : undefined,
    offerLocation: offerLocation || undefined,
    showUnsetClaims: showEmptyFields,
  };

  const content = (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Box
        data-testid="offer-preview-scroller"
        sx={{
          overflowY: shouldFillAvailableHeight ? 'scroll' : 'auto',
          overflowX: 'hidden',
          width: '100%',
          height: '100%',
          scrollbarGutter: shouldFillAvailableHeight ? 'stable' : undefined,
          scrollbarWidth: shouldFillAvailableHeight ? 'thin' : undefined,
          scrollbarColor: shouldFillAvailableHeight
            ? `${theme.palette.action.disabled} transparent`
            : undefined,
          ...(shouldFillAvailableHeight && {
            '&::-webkit-scrollbar': {
              width: 10,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.action.disabled,
              border: '2px solid transparent',
              borderRadius: 8,
              backgroundClip: 'content-box',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: theme.palette.action.disabledBackground,
            },
          }),
        }}
      >
        <Box
          data-testid="offer-preview-layout"
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: isMobile ? 3 : 2,
            width: '100%',
            mx: 'auto',
            p: isMobile ? 1.5 : 3,
            pt: isMobile ? 2 : 2,
            boxSizing: 'border-box',
            minHeight: shouldFillAvailableHeight ? '100%' : undefined,
          }}
        >

        {/* Left Info Panel */}
        <Box
          data-testid="offer-preview-details"
          sx={{
            flex: isMobile ? 'unset' : '1 1 0',
            minWidth: 0,
            background: theme.palette.background.paper,
            borderRadius: 3,
            boxShadow: 3,
            p: 3,
            maxHeight: shouldFillAvailableHeight ? 'none' : isMobile ? 'auto' : 600,
            alignSelf: shouldFillAvailableHeight ? 'stretch' : undefined,
            width: isMobile ? '100%' : undefined,
            boxSizing: 'border-box',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto', // Make content scrollable if it exceeds height
            pr: 1, // Add right padding for scrollbar
          }}>
            {details?.title && (
              <>
                <Typography variant="caption" color="textSecondary">
                  Offer title
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {details.title}
                </Typography>
                <Divider sx={{ my: 2 }} />
              </>
            )}

            {showBusinessAndType && (showEmptyFields || details?.businessName) && (
              <Typography variant="body1" gutterBottom>
                <strong>Business:</strong>{' '}
                {details?.businessName || <EmptyField width="45%" />}
              </Typography>
            )}

            {showBusinessAndType && (showEmptyFields || details?.type) && (
              <Typography variant="body1" gutterBottom>
                <strong>Offer type:</strong>{' '}
                {details?.type || <EmptyField width="35%" />}
              </Typography>
            )}

            {(showEmptyFields || locationLabel) && (
              <Typography variant="body1" gutterBottom>
                <strong>Location:</strong>{' '}
                {locationLabel || <EmptyField width="50%" />}
              </Typography>
            )}

            {(showEmptyFields || locationLabel || (showBusinessAndType && (details?.businessName || details?.type))) && (
              <Divider sx={{ my: 2 }} />
            )}

            <Typography variant="caption" color="textSecondary">
              Customer headline
            </Typography>
            <Typography variant="h6" gutterBottom>
              {details?.headline1 || <Skeleton width="70%" />}
            </Typography>

            {(showEmptyFields || details?.headline2) && (
              <Typography variant="subtitle1" gutterBottom color="textSecondary">
                {details?.headline2 || <EmptyField width="60%" />}
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="body1" gutterBottom sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
              <strong>Description:</strong>{' '}
              {details?.description ? details.description : <Skeleton width="95%" />}
            </Typography>

            <RedemptionCodeDisplay codes={details} showEmptyFields={showEmptyFields} />

            <Typography variant="body1" gutterBottom>
              <strong>Website:</strong>{' '}
              {details?.siteUrl ? (
                <a
                  href={siteHref(details.siteUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {details.siteUrl}
                </a>
              ) : (
                <Skeleton width="55%" />
              )}
            </Typography>

            <OfferSchedule
              startAt={previewStartDate}
              endAt={previewEndDate}
              showEmptyFields={showEmptyFields}
            />

            <Typography variant="body1" gutterBottom>
              <strong>Budget:</strong>{' '}
              {budgetLabel || <EmptyField width="30%" />}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>Max claims:</strong>{' '}
              {claimsLabel || <EmptyField width="25%" />}
            </Typography>

            {(showEmptyFields || details?.termsAndConditions) && (
              <Typography variant="body1" gutterBottom>
                <strong>Terms & conditions:</strong>{' '}
                {details?.termsAndConditions || <EmptyField width="65%" />}
              </Typography>
            )}

            {(showEmptyFields || (Array.isArray(details?.keywords) && details.keywords.length > 0)) && (
              <Box my={2} sx={{ display: 'flex', flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" sx={{ width: '100%' }}>
                  Keywords:
                </Typography>
                {Array.isArray(details?.keywords) && details.keywords.length > 0
                  ? details.keywords.map((kw, idx) => (
                      <Box key={idx} sx={pillSx(theme)}>
                        {kw}
                      </Box>
                    ))
                  : <EmptyField width="45%" />}
              </Box>
            )}

            {(categories.length > 0 || fulfillmentLabel) && (
              <Box my={2}>
                {categories.length > 0 && (
                  <>
                    <Typography variant="subtitle1">
                      Categories:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                      {categories.map((cat, idx) => (
                        <Box key={idx} sx={pillSx(theme)}>
                          {cat.name}
                        </Box>
                      ))}
                    </Box>
                  </>
                )}

                {fulfillmentLabel && (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ mt: categories.length ? 1.5 : 0 }}
                    >
                      Fulfillment:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                      <Box sx={pillSx(theme)}>
                        {fulfillmentLabel}
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Right Offer Card */}
        <Box
          data-testid="offer-preview-card"
          sx={{
            flex: isMobile ? 'unset' : '0 0 auto',
            background: theme.palette.background.paper,
            borderRadius: 3,
            boxShadow: 3,
            p: 3,
            width: isMobile ? '100%' : undefined,
            maxWidth: isMobile ? '100%' : 360,
            boxSizing: 'border-box',
            position: shouldFillAvailableHeight ? 'sticky' : undefined,
            top: shouldFillAvailableHeight ? 16 : undefined,
            alignSelf: shouldFillAvailableHeight ? 'flex-start' : undefined,
          }}
        >
          <OfferImagePreviewStatus {...imagePreview} />
          <OfferCard {...offerCardProps} />
        </Box>
        </Box>
      </Box>
    </Box>
  );

  if (variant === 'embedded') {
    return content;
  }

  return (
    <CustomDialog fullWidth maxWidth="md" open={true} onClose={onClose}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        {/* Close Button - Fixed position relative to dialog */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 20,
            background: theme.palette.background.paper,
            boxShadow: 2,
            '&:hover': { background: theme.palette.action.hover },
          }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
        {content}
      </Box>
    </CustomDialog>
  );
};

export default OfferPreviewCard;
